// Marketplace search positions (issue #217) — tracker table over immutable
// first-page snapshots. Ranks shown are organic unless the
// column says otherwise; a keyword whose latest capture failed is flagged, not
// interpreted as a position drop. Clicking a row opens the keyword's own page
// with the full channel rankings.

import { useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"
import { ChevronRight } from "lucide-react"
import type {
  SearchRankingDay,
  SearchRankingKeywordSummary,
  SearchRankingMarketplace,
} from "@nova-ai/shared"
import { Table, type TableColumn } from "../ui/table"
import { Tooltip } from "../ui/tooltip"
import { cn } from "../../lib/utils"
import {
  ORGANIC_RANK_HINT,
  SPONSORED_RANK_HINT,
  latestOrganicMove,
  rankHistorySummary,
} from "./search-ranking-keyword-detail.derive"

type SearchRankingViewProps = {
  marketplace: SearchRankingMarketplace
  keywords: SearchRankingKeywordSummary[]
}

export function SearchRankingView({ marketplace, keywords }: SearchRankingViewProps) {
  const navigate = useNavigate()

  const columns = useMemo(() => buildColumns(), [])

  return (
    <div>
      <Table
        columns={columns}
        rows={keywords}
        rowKey={(row) => row.phrase}
        cellAlign="middle"
        rowSeparator={(current, previous) =>
          previous?.category !== current.category ? current.category : null
        }
        onRowClick={(row) => {
          void navigate({
            to: "/keywords/$marketplace/$phrase",
            params: { marketplace: marketplace.id, phrase: row.phrase },
          })
        }}
        empty="No tracked keywords yet."
      />
    </div>
  )
}

function buildColumns(): readonly TableColumn<SearchRankingKeywordSummary>[] {
  return [
    {
      key: "phrase",
      header: "Keyword",
      render: (row) => (
        <span className="inline-flex items-center gap-1.5">
          <span className="font-medium">{row.phrase}</span>

          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/50" />
        </span>
      ),
    },
    // The two current-position columns sit together, then the two that describe
    // how the organic one moved. Reading left to right: where we are, then how we
    // got here.
    {
      key: "position",
      header: "Organic #",
      align: "right",
      headerHint: ORGANIC_RANK_HINT,
      render: (row) => <PositionCell summary={row} />,
    },
    {
      key: "ad",
      header: "Ad #",
      align: "right",
      headerHint: SPONSORED_RANK_HINT,
      render: (row) => <AdPositionCell summary={row} />,
    },
    {
      key: "change",
      header: "Change",
      align: "right",
      headerHint: `${KEYWORD_MOVE_HINT}. ▲ is a move up. Days we could not measure are skipped, not counted as a drop.`,
      render: (row) => <ChangeCell history={row.history} />,
    },
    {
      key: "trend",
      header: "Trend",
      headerHint:
        "Best organic position per measured day over the last 30 days, #1 at the top. A hollow dot on the bottom edge is a day we measured but were not on page 1. The line is tinted by the move across the whole window, not the last day.",
      render: (row) => <TrendSparkline history={row.history} />,
    },
  ]
}

/**
 * A blocked capture is not a rank drop, and both rank columns render `—` for it —
 * the same glyph they use for "not on page 1". Marking the unmeasured case is the
 * only thing keeping those two apart now that the status column is gone.
 */
function BlockedRank() {
  return (
    <Tooltip content="Latest capture was blocked — the position is unknown, not lost">
      <span className="cursor-help font-semibold text-warning">~</span>
    </Tooltip>
  )
}

function PositionCell({ summary }: { summary: SearchRankingKeywordSummary }) {
  const latest = summary.latest
  if (latest && !latest.measured) {
    return <BlockedRank />
  }
  const rank = latest?.bestOrganicRank ?? null
  if (rank === null) {
    return <span className="text-muted-foreground/50">—</span>
  }
  return <span className="text-sm font-semibold tabular-nums">#{rank}</span>
}

function AdPositionCell({ summary }: { summary: SearchRankingKeywordSummary }) {
  const latest = summary.latest
  if (latest && !latest.measured) {
    return <BlockedRank />
  }
  const rank = latest?.bestSponsoredRank ?? null
  if (rank === null) {
    return <span className="text-muted-foreground/50">—</span>
  }
  return <span className="tabular-nums text-muted-foreground">#{rank}</span>
}

/** Keyword-level move, day over day. The keyword detail's card rows show an
 *  identical-looking arrow for a single product's move, so both spell out which
 *  one they are — the two legitimately disagree when a different product takes
 *  over the best slot. */
const KEYWORD_MOVE_HINT =
  "Our best organic position for this keyword, versus the previous measured day"

function ChangeCell({ history }: { history: SearchRankingDay[] }) {
  const move = latestOrganicMove(history)
  if (move === null) {
    return <span className="text-muted-foreground/50">—</span>
  }
  if (move.kind === "lost") {
    return (
      <span className="font-semibold text-destructive" title="Dropped off the first page">
        ▼ off
      </span>
    )
  }
  if (move.kind === "entered") {
    return (
      <span className="font-semibold text-success" title="Back on the first page">
        ▲ new
      </span>
    )
  }
  if (move.delta === 0) {
    return <span className="tabular-nums text-muted-foreground/70">·</span>
  }
  return (
    <span
      className={cn(
        "font-semibold tabular-nums",
        move.delta > 0 ? "text-success" : "text-destructive"
      )}
      title={KEYWORD_MOVE_HINT}
    >
      {move.delta > 0 ? `▲ ${move.delta}` : `▼ ${-move.delta}`}
    </span>
  )
}

// Tiny inverted rank trend (#1 at the top): one dot per measured day joined by
// a line tinted by overall direction. A measured day without an organic
// position sits on the bottom edge as a hollow dot.
function TrendSparkline({ history }: { history: SearchRankingDay[] }) {
  const measured = history.filter((day) => day.measured)
  if (measured.length < 2) {
    return <span className="text-xs text-muted-foreground/50">—</span>
  }
  const width = 84
  const height = 24
  const ranks = measured.map((day) => day.bestOrganicRank)
  const known = ranks.filter((rank): rank is number => rank !== null)
  const worst = Math.max(...(known.length > 0 ? known : [1]), 4)
  const x = (index: number) => 5 + (width - 10) * (index / (measured.length - 1))
  const y = (rank: number | null) => 4 + (height - 8) * (((rank ?? worst + 1) - 1) / worst)
  // Coloured by the move across the whole window, not by the last day: the line
  // draws the period, so colouring it from one step made a week-long slide read
  // green whenever the final day ticked up. Day-over-day is the Change column's
  // job, and it sits right next to this.
  const periodChange = rankHistorySummary(history).rankChange
  const lineClass = trendLineClass(periodChange)
  const title = [
    periodTitle(periodChange),
    ...measured.map(
      (day) =>
        `${day.day.slice(5)}: ${day.bestOrganicRank === null ? "—" : `#${day.bestOrganicRank}`}`
    ),
  ].join("  ")
  return (
    <svg width={width} height={height} aria-hidden="true" className="block">
      <title>{title}</title>

      <polyline
        points={ranks.map((rank, index) => `${x(index)},${y(rank)}`).join(" ")}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        className={lineClass}
      />

      {ranks.map((rank, index) => (
        <circle
          key={index}
          cx={x(index)}
          cy={y(rank)}
          r={index === ranks.length - 1 ? 3 : 2}
          className={trendDotClass(rank, index === ranks.length - 1)}
        />
      ))}
    </svg>
  )
}

function trendLineClass(periodChange: number | null): string {
  if (periodChange === null || periodChange === 0) {
    return "text-border"
  }
  return periodChange > 0 ? "text-success/60" : "text-destructive/60"
}

function periodTitle(periodChange: number | null): string {
  if (periodChange === null) {
    return "Best organic position per day."
  }
  if (periodChange === 0) {
    return "Best organic position per day — unchanged over the window."
  }
  return `Best organic position per day — ${periodChange > 0 ? "up" : "down"} ${Math.abs(periodChange)} over the window.`
}

function trendDotClass(rank: number | null, isLast: boolean): string {
  if (rank === null) {
    return "fill-card stroke-muted-foreground"
  }
  return isLast ? "fill-info" : "fill-muted-foreground/50"
}
